import { BackendApiClient } from './backend-client';
import type { ScopeContext } from '../model/types';
import { secureSessionStorage } from '../../../shared/lib/storage';
import { constructAuthMessage, getCurrentTimestamp, sign, signatureToBase64 } from '../../../shared/lib/crypto';
import { DebugLogger } from '../../../shared/lib/debug';
import { acceptLanguage } from '@/shared/lib/accept-language';
import { requireList } from '../../../shared/lib/response-shape';
import {
  BackendValidationError,
  backendValidationErrorFrom,
  type BackendError,
  type BackendErrorResponse,
} from '../../../shared/lib/backend-errors';

export { BackendValidationError };
export type { BackendError, BackendErrorResponse };

const backendClient = new BackendApiClient();
const API_BASE_URL = '/api'; // Use proxy in both development (Vite) and production (nginx)

export interface Investor {
  id: string;
  account_id: string;
  name: string;
  tax_id: string;
  status: 'active' | 'inactive' | 'pending';
  tags: string[];
  created: string;
}

export interface InvestorResponse {
  investors: Investor[];
  cursor?: string;
}

export interface CreateInvestorRequest {
  investors: Array<{
    name: string;
    tax_id: string;
    /** Omit to create no portfolio. The backend caps this at one entry. */
    portfolios?: Array<{ name: string; unique_id: string; tags?: string[] }>;
  }>;
}

export interface CreateInvestorResponse {
  investors?: Investor[];
  message?: string;
  [key: string]: any;
}

/**
 * Get investors for a specific account scope
 * @param sessionId - The session ID for authentication
 * @param scopeContext - The scope context with scopeId and scopeType (must be account-level)
 * @returns Promise<InvestorResponse> - The list of investors
 */
export async function getInvestors(
  sessionId: string,
  scopeContext: ScopeContext
): Promise<InvestorResponse> {
  try {
    if (!scopeContext.scopeId || !scopeContext.scopeType) {
      throw new Error('Scope context must include scopeId and scopeType');
    }

    if (scopeContext.scopeType !== 'account') {
      throw new Error('Investor endpoint requires account-level scope');
    }

    const response = await backendClient.get('/investor', sessionId, scopeContext);

    if (response.success && response.data) {
      const body = response.data as InvestorResponse;
      return {
        ...body,
        investors: requireList<Investor>(body, 'investors', 'Unreadable investor response'),
      };
    } else {
      throw new Error(response.error || 'Failed to fetch investors');
    }
  } catch (error) {
    // Re-throw the error with its original message (backend error already included)
    throw error;
  }
}

/**
 * Create session auth headers for investor API calls
 */
async function createInvestorAuthHeaders(
  sessionId: string,
  body?: any,
  scopeContext?: ScopeContext
): Promise<Record<string, string>> {
  const sessionPrivateKey = await secureSessionStorage.getSessionKey(sessionId);
  
  if (!sessionPrivateKey) {
    throw new Error('Session not found or expired');
  }

  const currentTime = getCurrentTimestamp();
  const message = constructAuthMessage(sessionId, currentTime, body);
  const signature = sign(sessionPrivateKey, message);
  const signatureBase64 = signatureToBase64(signature);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Access-Time': currentTime,
    'User-Id': sessionId,
    'User-Type': 'session',
    'Access-Signature': signatureBase64,
    'Accept-Language': acceptLanguage(),
  };

  // Add scope headers if scope context is provided
  if (scopeContext?.scopeId && scopeContext?.scopeType) {
    headers['Scope-Id'] = scopeContext.scopeId;
    headers['Scope-Type'] = scopeContext.scopeType;
  }

  return headers;
}

/**
 * Create a new investor
 * @param sessionId - The session ID for authentication
 * @param request - The investor creation request
 * @param scopeContext - The scope context with scopeId and scopeType (must be account-level)
 * @returns Promise<CreateInvestorResponse> - The created investor(s)
 */
export async function createInvestor(
  sessionId: string,
  request: CreateInvestorRequest,
  scopeContext: ScopeContext
): Promise<CreateInvestorResponse> {
  try {
    if (!scopeContext.scopeId || !scopeContext.scopeType) {
      throw new Error('Scope context must include scopeId and scopeType');
    }

    if (scopeContext.scopeType !== 'account') {
      throw new Error('Investor endpoint requires account-level scope');
    }

    const headers = await createInvestorAuthHeaders(sessionId, request, scopeContext);
    const url = `${API_BASE_URL}/investor`;

    DebugLogger.request('POST', url, headers, request);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      mode: 'cors',
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: 'Unknown error' };
      }

      DebugLogger.response('POST', url, response.status, response.statusText, errorData);
      DebugLogger.error('Backend error response', {
        status: response.status,
        statusText: response.statusText,
        errorData: JSON.stringify(errorData, null, 2),
        requestUrl: url,
        requestMethod: 'POST',
        sessionId: sessionId
      });

      throw backendValidationErrorFrom(errorData, `HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();
    DebugLogger.response('POST', url, response.status, response.statusText, responseData);
    
    return responseData as CreateInvestorResponse;
  } catch (error) {
    if (error instanceof BackendValidationError) {
      throw error;
    }
    throw new BackendValidationError(
      `Failed to create investor: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

